import {
    getPredefinedGroupLabel,
    orderedPredefinedLeafTypes,
} from "@common/constants/domains/accounting/accountGroups";
import {
    AccountClass,
    AccountType,
} from "@common/constants/domains/accounting/accounts";
import {
    getAccountClassForType,
    orderedAccountClasses,
} from "@common/constants/domains/accounting/helpers";
import {
    Account,
    AccountGroup,
} from "@common/types/domains/accounting/account";
import { mergeGroupsForDisplay } from "@common/helpers/domains/accounting/entityGroupMerge";
import { UNGROUPED_PARENT_IDENTITY } from "@common/helpers/domains/accounting/chartRowDisplayKeys";
import { resolveMergedAccountSortOrder } from "@common/helpers/domains/accounting/chartRowShaping";
import { compareScopeOrder } from "@common/helpers/domains/accounting/chartScopeOrdering";

export type AccountWithEntity = Account & { entityId: number };

export type CoaAccountTreeNodeKind = "class" | "type" | "group" | "account";

export interface CoaAccountTreeNode {
    id: string;
    kind: CoaAccountTreeNodeKind;
    label: string;
    sortOrder: number;
    accountIds: string[];
    searchValues: string[];
    children: CoaAccountTreeNode[];
}

interface BuildCoaAccountTreeParams {
    accounts: AccountWithEntity[] | undefined;
    groups: AccountGroup[] | undefined;
    entityIds?: number[];
}

interface AccountBucket {
    name: string;
    type: AccountType;
    parentIdentity: string;
    accounts: AccountWithEntity[];
}

const createNodeId = (kind: CoaAccountTreeNodeKind, path: string[]): string =>
    JSON.stringify([kind, ...path]);

const getDescendantAccountIds = (children: CoaAccountTreeNode[]): string[] =>
    children.flatMap((child) => child.accountIds);

const compareScopeNodes = (
    first: CoaAccountTreeNode,
    second: CoaAccountTreeNode,
): number => {
    if (
        (first.kind !== "account" && first.kind !== "group") ||
        (second.kind !== "account" && second.kind !== "group")
    ) {
        return 0;
    }
    return compareScopeOrder(
        {
            id: first.id,
            kind: first.kind,
            name: first.label,
            sortOrder: first.sortOrder,
        },
        {
            id: second.id,
            kind: second.kind,
            name: second.label,
            sortOrder: second.sortOrder,
        },
    );
};

export function buildCoaAccountTree({
    accounts,
    groups,
    entityIds,
}: BuildCoaAccountTreeParams): CoaAccountTreeNode[] {
    if (!accounts || !groups) {
        return [];
    }

    const selectedEntityIds = entityIds ? new Set(entityIds) : undefined;
    const scopedAccounts = selectedEntityIds
        ? accounts.filter((account) => selectedEntityIds.has(account.entityId))
        : accounts;
    const scopedGroups = selectedEntityIds
        ? groups.filter((group) => selectedEntityIds.has(group.entityId))
        : groups;
    const { groupsById, mergedGroupsByParentKey, groupKeyById } =
        mergeGroupsForDisplay({
            groups: scopedGroups,
            selectedEntityIds: entityIds,
        });
    const mergedGroupsByKey = new Map(
        [...mergedGroupsByParentKey.values()]
            .flat()
            .map((group) => [group.key, group]),
    );

    const parentIdentityOf = (account: AccountWithEntity): string =>
        account.parentGroupId && groupsById.has(account.parentGroupId)
            ? (groupKeyById.get(account.parentGroupId) ??
              UNGROUPED_PARENT_IDENTITY)
            : UNGROUPED_PARENT_IDENTITY;

    const bucketsById = new Map<string, AccountBucket>();
    for (const account of scopedAccounts) {
        const parentIdentity = parentIdentityOf(account);
        const id = createNodeId("account", [
            account.type,
            parentIdentity,
            account.name,
        ]);
        const existing = bucketsById.get(id);
        if (existing) {
            existing.accounts.push(account);
        } else {
            bucketsById.set(id, {
                name: account.name,
                type: account.type,
                parentIdentity,
                accounts: [account],
            });
        }
    }

    const bucketsByParentIdentity = new Map<string, AccountBucket[]>();
    for (const bucket of bucketsById.values()) {
        const siblings =
            bucketsByParentIdentity.get(bucket.parentIdentity) ?? [];
        siblings.push(bucket);
        bucketsByParentIdentity.set(bucket.parentIdentity, siblings);
    }

    const buildAccountNode = (bucket: AccountBucket): CoaAccountTreeNode => ({
        id: createNodeId("account", [
            bucket.type,
            bucket.parentIdentity,
            bucket.name,
        ]),
        kind: "account",
        label: bucket.name,
        sortOrder: resolveMergedAccountSortOrder(
            bucket.accounts.map((account) => account.sortOrder),
        ),
        accountIds: bucket.accounts.map((account) => account.id),
        searchValues: bucket.accounts.flatMap((account) => [
            account.name,
            account.codeText ?? "",
        ]),
        children: [],
    });

    const buildGroupNode = (
        groupKey: string,
        type: AccountType,
    ): CoaAccountTreeNode | undefined => {
        const group = mergedGroupsByKey.get(groupKey);
        if (!group) {
            return undefined;
        }
        const childGroups = (mergedGroupsByParentKey.get(group.key) ?? [])
            .map((child) => buildGroupNode(child.key, type))
            .filter(
                (child): child is CoaAccountTreeNode => child !== undefined,
            );
        const childAccounts = (bucketsByParentIdentity.get(group.key) ?? [])
            .filter((bucket) => bucket.type === type)
            .map(buildAccountNode);
        const children = [...childGroups, ...childAccounts].sort(
            compareScopeNodes,
        );
        if (children.length === 0) {
            return undefined;
        }
        return {
            id: createNodeId("group", [group.key]),
            kind: "group",
            label: group.group.name,
            sortOrder: group.mergedSortOrder,
            accountIds: getDescendantAccountIds(children),
            searchValues: group.groups.map((item) => item.name),
            children,
        };
    };

    const presentTypes = new Set(scopedAccounts.map((account) => account.type));
    const typesByClass = new Map<AccountClass, CoaAccountTreeNode[]>();
    for (const type of orderedPredefinedLeafTypes) {
        if (!presentTypes.has(type)) {
            continue;
        }
        const groupNodes = (mergedGroupsByParentKey.get(null) ?? [])
            .filter((group) => group.group.type === type)
            .map((group) => buildGroupNode(group.key, type))
            .filter(
                (group): group is CoaAccountTreeNode => group !== undefined,
            );
        const accountNodes = (
            bucketsByParentIdentity.get(UNGROUPED_PARENT_IDENTITY) ?? []
        )
            .filter((bucket) => bucket.type === type)
            .map(buildAccountNode);
        const children = [...groupNodes, ...accountNodes].sort(
            compareScopeNodes,
        );
        if (children.length === 0) {
            continue;
        }
        const accountClass = getAccountClassForType(type);
        const typeNode: CoaAccountTreeNode = {
            id: createNodeId("type", [accountClass, type]),
            kind: "type",
            label: getPredefinedGroupLabel(type),
            sortOrder: 0,
            accountIds: getDescendantAccountIds(children),
            searchValues: [type, getPredefinedGroupLabel(type)],
            children,
        };
        const classTypes = typesByClass.get(accountClass) ?? [];
        classTypes.push(typeNode);
        typesByClass.set(accountClass, classTypes);
    }

    return orderedAccountClasses.flatMap((accountClass) => {
        const children = typesByClass.get(accountClass);
        if (!children) {
            return [];
        }
        return [
            {
                id: createNodeId("class", [accountClass]),
                kind: "class",
                label: accountClass,
                sortOrder: 0,
                accountIds: getDescendantAccountIds(children),
                searchValues: [accountClass],
                children,
            },
        ];
    });
}

export const isCoaAccountNodeMatch = (
    node: CoaAccountTreeNode,
    search: string,
): boolean => {
    const query = search.trim().toLowerCase();
    return node.searchValues.some((value) =>
        value.toLowerCase().includes(query),
    );
};

export function filterCoaAccountTree(
    nodes: CoaAccountTreeNode[],
    search: string,
): CoaAccountTreeNode[] {
    if (!search.trim()) {
        return nodes;
    }
    return nodes.flatMap((node) => {
        if (isCoaAccountNodeMatch(node, search)) {
            return [node];
        }
        const children = filterCoaAccountTree(node.children, search);
        return children.length > 0
            ? [
                  {
                      ...node,
                      children,
                      accountIds: getDescendantAccountIds(children),
                  },
              ]
            : [];
    });
}
